import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DoCheck,
    ElementRef,
    Input,
    OnChanges,
    OnInit,
    ViewChild,
} from '@angular/core';
import {fromEvent, Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {CheckboxTreeNode} from 'app/shared/components/checkbox-tree/checkbox-tree.interface';

@Component({
    selector: 'enum-output',
    templateUrl: './enum-output.component.html',
    styleUrls: ['./enum-output.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
/* eslint-disable @angular-eslint/no-conflicting-lifecycle */
export class EnumOutputComponent implements OnInit, OnChanges, DoCheck {
    @Input() enumData: Iterable<string | CheckboxTreeNode>;
    @Input() restStringFn: (restCount: number) => string;
    @ViewChild('outputEl', {static: true}) $outputEl: ElementRef<HTMLOutputElement>;
    totalCount = 0;
    otherCount = 0;
    hiddenItems: Array<string>;
    enumItems: Set<string | CheckboxTreeNode>;
    private resizeSubscription: Subscription;

    get list() {
        if (this.enumItems && this.enumItems.size !== this.totalCount) {
            this.totalCount = this.enumItems.size;
            this.onEnumSizeChange();
        }
        return this.enumItems;
    }

    trackByFn = (index, value) => value;

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit() {
        this.resizeSubscription = fromEvent(window, 'resize')
            .pipe(debounceTime(100))
            .subscribe(() => {
                this.onEnumSizeChange();
            });
    }

    ngOnChanges() {
        this.enumItems = new Set(this.enumData);
    }

    ngDoCheck() {
        if (this.totalCount !== this.enumItems.size) {
            this.cd.markForCheck();
        }
    }

    onEnumSizeChange() {
        const prevValue = this.otherCount;
        this.resetHidden();
        setTimeout(() => this.hideOverflowed(prevValue), 0);
    }

    getRestString(): string {
        if (typeof this.restStringFn === 'function') {
            return this.restStringFn(this.otherCount);
        }
        return this.otherCount.toString();
    }

    resetHidden() {
        this.$outputEl.nativeElement.classList.add('under-calculation');
        let hidden = this.$outputEl.nativeElement.querySelectorAll<HTMLSpanElement>('[data-hidden="true"]');
        for (let el of hidden) {
            el.dataset.hidden = 'false';
        }
        this.hiddenItems = [];
        this.otherCount = 0;
        this.cd.markForCheck();
    }

    private *getChildrenIterator(children: HTMLCollection): IterableIterator<HTMLElement> {
        for (let i = 0, l = children.length; i < l; i++) {
            yield children.item(i) as HTMLElement;
        }
    }

    hideOverflowed(prevOtherCount) {
        const margin = 0;
        let outputEl = this.$outputEl.nativeElement;
        const totalClientWidth = outputEl.clientWidth;
        if (outputEl.scrollWidth > totalClientWidth) {
            let children = [...this.getChildrenIterator(outputEl.children)] as Array<HTMLSpanElement>;
            let restEl = children.pop();
            let widths = [];
            let totalWidth = children.reduce((total, child) => {
                let width = child.clientWidth;
                widths.push(width);
                total += width;
                return total;
            }, restEl.clientWidth);

            if (children.length !== 1) {
                while (totalWidth > totalClientWidth - margin) {
                    let elementToHide = children.pop();
                    elementToHide.dataset.hidden = 'true';
                    totalWidth -= widths.pop();
                    this.hiddenItems.push(elementToHide.firstElementChild.textContent);
                    this.otherCount++;
                }
            }
            this.hiddenItems.reverse();
            this.cd.detectChanges();
        } else if (prevOtherCount !== this.otherCount) {
            this.cd.markForCheck();
        }
        outputEl.classList.remove('under-calculation');
    }
}
